import { useCallback, useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

// M05 §7/§8 (T060) — Vendor Master. Full CRUD over the existing backend
// `@Controller('v1/master/vendors')`:
//   GET  /master/vendors?q&status&page&limit → { data, total }
//   POST /master/vendors (CreateVendorDto) · PUT /master/vendors/:id
//   PATCH /master/vendors/:id/deactivate
// Vendors are maker-checker governed (default status PENDING_APPROVAL) — see the
// Master Approval Dashboard for the checker step. Client validation mirrors the
// backend regexes (mobile) and numeric bounds (performanceRating 0-5).

interface Vendor {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly legalName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly performanceRating: number | null;
  readonly recordStatusCode: string;
}

const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED', 'PENDING_APPROVAL', 'REJECTED'] as const;

const MOBILE_RE = /^[6-9][0-9]{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const EMPTY = {
  vendorCode: '', vendorName: '', legalName: '', contactPerson: '', mobileNumber: '',
  email: '', address: '', gstNumber: '', panNumber: '', bankAccountNumber: '', ifscCode: '',
  workSpecializations: '', serviceZones: '', territoryCoverage: '', performanceRating: '',
  tdsSectionCode: '', ldcCertificateNo: '', ldcRatePct: '', ldcValidFrom: '', ldcValidTo: '', ldcAmountLimit: '',
};
type Form = typeof EMPTY;

/** A TDS section in force today (GET /master/vendors/tds-sections). */
interface TdsSectionOption { readonly sectionCode: string; readonly description: string }

const PAGE_SIZE = 20;
const csv = (x: string): string[] | undefined => {
  const arr = x.split(',').map((v) => v.trim()).filter(Boolean);
  return arr.length ? arr : undefined;
};

export function VendorMasterPage(): ReactNode {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Vendor[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tdsSections, setTdsSections] = useState<readonly TdsSectionOption[]>([]);

  // The sections a vendor may be put under are the tenant's own, kept on
  // Company Tax Configuration.
  useEffect(() => {
    if (!showForm) return;
    apiRequest<TdsSectionOption[]>('/master/vendors/tds-sections', { token })
      .then(setTdsSections)
      .catch((e) => setFormError((e as Error).message));
  }, [showForm, token]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (q.trim()) params.set('q', q.trim());
      if (statusFilter) params.set('status', statusFilter);
      const r = await apiRequest<{ data: Vendor[]; total: number }>(
        `/master/vendors?${params.toString()}`, { token },
      );
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, page, q, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Creation lives on the one canonical page (/admin/vendors/new). This screen
  // keeps list, edit and deactivate only, so a vendor can never be captured
  // with a hand-typed code or a performance rating it has not yet earned.
  const beginEdit = useCallback(async (v: Vendor) => {
    setForm({
      ...EMPTY,
      vendorCode: v.vendorCode, vendorName: v.vendorName, legalName: v.legalName,
      contactPerson: v.contactPerson, mobileNumber: v.mobileNumber,
      performanceRating: v.performanceRating == null ? '' : String(v.performanceRating),
    });
    setEditingId(v.vendorId); setFormError(null); setShowForm(true);
    try {
      const full = await apiRequest<Record<string, unknown>>(
        `/master/vendors/${encodeURIComponent(v.vendorId)}`, { token },
      );
      const arr = (k: string) => Array.isArray(full[k]) ? (full[k] as string[]).join(', ') : '';
      const str = (k: string) => (full[k] == null ? '' : String(full[k]));
      setForm((f) => ({
        ...f,
        vendorName: str('vendorName') || f.vendorName,
        legalName: str('legalName') || f.legalName,
        contactPerson: str('contactPerson') || f.contactPerson,
        mobileNumber: str('mobileNumber') || f.mobileNumber,
        email: str('email'), address: str('address'),
        gstNumber: str('gstNumber'), panNumber: str('panNumber'),
        bankAccountNumber: str('bankAccountNumber'), ifscCode: str('ifscCode'),
        workSpecializations: arr('workSpecializations'), serviceZones: arr('serviceZones'),
        territoryCoverage: arr('territoryCoverage'),
        performanceRating: full.performanceRating == null ? '' : String(full.performanceRating),
        tdsSectionCode: str('tdsSectionCode'), ldcCertificateNo: str('ldcCertificateNo'),
        ldcRatePct: str('ldcRatePct'), ldcValidFrom: str('ldcValidFrom'),
        ldcValidTo: str('ldcValidTo'), ldcAmountLimit: str('ldcAmountLimit'),
      }));
    } catch { /* keep summary values if the detail fetch fails */ }
  }, [token]);

  const clientValidate = (): string | null => {
    if (!MOBILE_RE.test(form.mobileNumber)) return t('mdm.common.invalidMobile');
    if (form.email && !EMAIL_RE.test(form.email)) return t('mdm.common.invalidEmail');
    if (form.ifscCode && !IFSC_RE.test(form.ifscCode)) return t('mdm.vendor.invalidIfsc');
    if (form.performanceRating) {
      const n = Number(form.performanceRating);
      if (Number.isNaN(n) || n < 0 || n > 5) return t('mdm.vendor.invalidRating');
    }
    // A lower-deduction certificate is recorded whole or not at all.
    const certificate = [form.ldcCertificateNo, form.ldcRatePct, form.ldcValidFrom, form.ldcValidTo, form.ldcAmountLimit];
    const given = certificate.filter((v) => v.trim()).length;
    if (given > 0 && given < certificate.length) return t('mdm.vendor.ldcIncomplete');
    if (given > 0 && !form.tdsSectionCode) return t('mdm.vendor.ldcNeedsSection');
    return null;
  };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const v = clientValidate();
    if (v) { setFormError(v); return; }
    setSaving(true); setFormError(null);
    const trimmed = (x: string) => (x.trim() ? x.trim() : undefined);
    const rating = form.performanceRating.trim() ? Number(form.performanceRating) : undefined;
    try {
      if (editingId) {
        await apiRequest(`/master/vendors/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: {
            vendorName: form.vendorName.trim(), legalName: form.legalName.trim(),
            contactPerson: form.contactPerson.trim(), mobileNumber: form.mobileNumber.trim(),
            email: trimmed(form.email), address: trimmed(form.address),
            gstNumber: trimmed(form.gstNumber), panNumber: trimmed(form.panNumber),
            bankAccountNumber: trimmed(form.bankAccountNumber), ifscCode: trimmed(form.ifscCode),
            workSpecializations: csv(form.workSpecializations), serviceZones: csv(form.serviceZones),
            territoryCoverage: csv(form.territoryCoverage), performanceRating: rating,
            // Sent even when blank, so a section or certificate can be taken off.
            tdsSectionCode: form.tdsSectionCode || null,
            ldcCertificateNo: form.ldcCertificateNo.trim() || null,
            ldcRatePct: form.ldcRatePct.trim() ? Number(form.ldcRatePct) : null,
            ldcValidFrom: form.ldcValidFrom || null, ldcValidTo: form.ldcValidTo || null,
            ldcAmountLimit: form.ldcAmountLimit.trim() ? Number(form.ldcAmountLimit) : null,
          },
        });
      } else {
        await apiRequest('/master/vendors', {
          method: 'POST', token,
          body: {
            vendorCode: form.vendorCode.trim(), vendorName: form.vendorName.trim(),
            legalName: form.legalName.trim(), contactPerson: form.contactPerson.trim(),
            mobileNumber: form.mobileNumber.trim(), email: trimmed(form.email),
            address: form.address.trim(), gstNumber: trimmed(form.gstNumber),
            panNumber: trimmed(form.panNumber), bankAccountNumber: trimmed(form.bankAccountNumber),
            ifscCode: trimmed(form.ifscCode), workSpecializations: csv(form.workSpecializations),
            serviceZones: csv(form.serviceZones), territoryCoverage: csv(form.territoryCoverage),
            performanceRating: rating,
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  const onDeactivate = useCallback(async (v: Vendor) => {
    setError(null);
    try {
      await apiRequest(`/master/vendors/${encodeURIComponent(v.vendorId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <MdmShell
      label="vendor-master"
      title={t('mdm.vendor.title')}
      description={t('mdm.vendor.desc')}
      actions={<Button type="button" variant="primary" data-testid="new-vendor" onClick={() => navigate('/admin/vendors/new')}>{t('mdm.vendor.newBtn')}</Button>}
    >
      <form aria-label="vendor-filters" onSubmit={(e) => { e.preventDefault(); setPage(1); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="ven-q" label={t('mdm.vendor.searchLabel')}>
          <input id="ven-q" value={q} onChange={(e) => setQ(e.target.value)} style={s.input} placeholder={t('mdm.vendor.searchPh')} />
        </Field>
        <Field id="ven-status" label={t('mdm.col.status')}>
          <select id="ven-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-vendor-form' : 'create-vendor-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.vendor.editTitle') : t('mdm.vendor.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="v-vendorCode" label={t('mdm.vendor.vendorCode')}>
              <input id="v-vendorCode" value={form.vendorCode} onChange={set('vendorCode')} required disabled={!!editingId} style={s.input} />
            </Field>
            <Field id="v-vendorName" label={t('mdm.vendor.vendorName')}>
              <input id="v-vendorName" value={form.vendorName} onChange={set('vendorName')} required style={s.input} />
            </Field>
            <Field id="v-legalName" label={t('mdm.vendor.legalName')}>
              <input id="v-legalName" value={form.legalName} onChange={set('legalName')} required style={s.input} />
            </Field>
            <Field id="v-contactPerson" label={t('mdm.vendor.contactPerson')}>
              <input id="v-contactPerson" value={form.contactPerson} onChange={set('contactPerson')} required style={s.input} />
            </Field>
            <Field id="v-mobileNumber" label={t('mdm.vendor.mobile')} hint={t('mdm.common.mobileHint')}>
              <input id="v-mobileNumber" value={form.mobileNumber} onChange={set('mobileNumber')} required style={s.input} />
            </Field>
            <Field id="v-email" label={t('mdm.vendor.email')}>
              <input id="v-email" value={form.email} onChange={set('email')} style={s.input} />
            </Field>
            <Field id="v-address" label={t('mdm.vendor.address')}>
              <input id="v-address" value={form.address} onChange={set('address')} required={!editingId} style={s.input} />
            </Field>
            <Field id="v-gstNumber" label={t('mdm.vendor.gst')}>
              <input id="v-gstNumber" value={form.gstNumber} onChange={set('gstNumber')} style={s.input} />
            </Field>
            <Field id="v-panNumber" label={t('mdm.vendor.pan')}>
              <input id="v-panNumber" value={form.panNumber} onChange={set('panNumber')} style={s.input} />
            </Field>
            <Field id="v-bankAccountNumber" label={t('mdm.vendor.bankAccount')}>
              <input id="v-bankAccountNumber" value={form.bankAccountNumber} onChange={set('bankAccountNumber')} style={s.input} />
            </Field>
            <Field id="v-ifscCode" label={t('mdm.vendor.ifsc')} hint={t('mdm.vendor.ifscHint')}>
              <input id="v-ifscCode" value={form.ifscCode} onChange={set('ifscCode')} style={s.input} />
            </Field>
            <Field id="v-workSpecializations" label={t('mdm.vendor.workSpec')} hint={t('mdm.vendor.csvHint')}>
              <input id="v-workSpecializations" value={form.workSpecializations} onChange={set('workSpecializations')} style={s.input} />
            </Field>
            <Field id="v-serviceZones" label={t('mdm.vendor.serviceZones')} hint={t('mdm.vendor.csvHint')}>
              <input id="v-serviceZones" value={form.serviceZones} onChange={set('serviceZones')} style={s.input} />
            </Field>
            <Field id="v-territoryCoverage" label={t('mdm.vendor.territoryCoverage')} hint={t('mdm.vendor.csvHint')}>
              <input id="v-territoryCoverage" value={form.territoryCoverage} onChange={set('territoryCoverage')} style={s.input} />
            </Field>
            <Field id="v-performanceRating" label={t('mdm.vendor.perfRating')} hint={t('mdm.vendor.perfRatingHint')}>
              <input id="v-performanceRating" type="number" min={0} max={5} step={0.1} value={form.performanceRating} onChange={set('performanceRating')} style={s.input} />
            </Field>
            <Field id="v-tdsSectionCode" label={t('mdm.vendor.tdsSection')} hint={t('mdm.vendor.tdsSectionHint')}>
              <select id="v-tdsSectionCode" value={form.tdsSectionCode} onChange={set('tdsSectionCode')} style={s.input}>
                <option value="">{t('mdm.vendor.tdsSectionNone')}</option>
                {/* A section no longer in force stays readable on the vendor it was set on. */}
                {form.tdsSectionCode && !tdsSections.some((x) => x.sectionCode === form.tdsSectionCode)
                  ? <option value={form.tdsSectionCode}>{form.tdsSectionCode}</option> : null}
                {tdsSections.map((x) => <option key={x.sectionCode} value={x.sectionCode}>{x.sectionCode} - {x.description}</option>)}
              </select>
            </Field>
            <Field id="v-ldcCertificateNo" label={t('mdm.vendor.ldcCertificateNo')} hint={t('mdm.vendor.ldcHint')}>
              <input id="v-ldcCertificateNo" value={form.ldcCertificateNo} onChange={set('ldcCertificateNo')} maxLength={50} style={s.input} />
            </Field>
            <Field id="v-ldcRatePct" label={t('mdm.vendor.ldcRate')}>
              <input id="v-ldcRatePct" type="number" min={0} max={100} step={0.01} value={form.ldcRatePct} onChange={set('ldcRatePct')} style={s.input} />
            </Field>
            <Field id="v-ldcValidFrom" label={t('mdm.vendor.ldcValidFrom')}>
              <DateInput id="v-ldcValidFrom" type="date" value={form.ldcValidFrom} onValueChange={(v) => setForm((f) => ({ ...f, ldcValidFrom: v }))} style={s.input} />
            </Field>
            <Field id="v-ldcValidTo" label={t('mdm.vendor.ldcValidTo')}>
              <DateInput id="v-ldcValidTo" type="date" value={form.ldcValidTo} onValueChange={(v) => setForm((f) => ({ ...f, ldcValidTo: v }))} style={s.input} />
            </Field>
            <Field id="v-ldcAmountLimit" label={t('mdm.vendor.ldcAmountLimit')}>
              <input id="v-ldcAmountLimit" type="number" min={0} step={0.01} value={form.ldcAmountLimit} onChange={set('ldcAmountLimit')} style={s.input} />
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-vendor" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.vendor.createBtn')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('mdm.common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('mdm.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="vendors-empty" style={{ color: s.muted }}>{t('mdm.vendor.empty')}</p>
      ) : (
        <>
          <DataTable<Vendor>
            ariaLabel="vendors-table"
            testId="vendors-table"
            rows={rows}
            rowKey={(v) => v.vendorId}
            rowTestId={(v) => `vendor-row-${v.vendorId}`}
            columns={[
              { key: 'vendorCode', header: t('mdm.vendor.colVendorCode'), render: (v) => v.vendorCode },
              { key: 'vendorName', header: t('mdm.vendor.colName'), render: (v) => v.vendorName },
              { key: 'legalName', header: t('mdm.vendor.colLegalName'), render: (v) => v.legalName },
              { key: 'contactPerson', header: t('mdm.vendor.colContact'), render: (v) => v.contactPerson },
              { key: 'mobileNumber', header: t('mdm.vendor.colMobile'), render: (v) => v.mobileNumber },
              { key: 'rating', header: t('mdm.vendor.colRating'), render: (v) => (v.performanceRating == null ? '—' : v.performanceRating) },
              { key: 'status', header: t('mdm.col.status'), render: (v) => <StatusBadge status={v.recordStatusCode} /> },
              {
                key: 'actions',
                header: t('mdm.col.actions'),
                render: (v) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button type="button" variant="secondary" data-testid={`edit-${v.vendorId}`} onClick={() => void beginEdit(v)}>{t('mdm.common.edit')}</Button>
                    {v.recordStatusCode === 'ACTIVE' && (
                      <Button type="button" variant="danger" data-testid={`deactivate-${v.vendorId}`} onClick={() => onDeactivate(v)}>{t('mdm.common.deactivate')}</Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <Button type="button" variant="secondary" disabled={page <= 1} data-testid="prev-page" onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('mdm.common.prev')}</Button>
            <span style={{ fontSize: 13, color: s.muted }} data-testid="page-info">{t('mdm.vendor.pageInfo', { page, pages, total })}</span>
            <Button type="button" variant="secondary" disabled={page >= pages} data-testid="next-page" onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('mdm.common.next')}</Button>
          </div>
        </>
      )}
    </MdmShell>
  );
}
